<template>
    <div class="storyline-wrap">
        <!-- ---------- Filters ---------- -->
        <div class="story-controls">
            <div class="story-toggle">
                <button
                    v-for="period in periodFilterOptions"
                    :key="`story-period-${period}`"
                    type="button"
                    class="story-toggle__btn"
                    :class="{ 'story-toggle__btn--active': selectedPeriod === period }"
                    @click="selectedPeriod = period"
                >
                    {{ formatPeriodFilterLabel(period) }}
                </button>
            </div>

            <div class="story-controls__row">
                <div class="story-toggle story-team-switch">
                    <button
                        v-for="option in teamFilterOptions"
                        :key="`story-team-${option.value}`"
                        type="button"
                        class="story-toggle__btn"
                        :class="{ 'story-toggle__btn--active': teamFilter === option.value }"
                        @click="setTeamFilter(option.value)"
                    >
                        <img v-if="option.logo" :src="option.logo" alt="" class="story-toggle__logo">
                        <span>{{ option.label }}</span>
                    </button>
                </div>
                <div class="story-toggle">
                    <button type="button" class="story-toggle__btn" :class="{ 'story-toggle__btn--active': !isDenseView }" @click="viewMode = 'full'">
                        Αναλυτικά
                    </button>
                    <button type="button" class="story-toggle__btn" :class="{ 'story-toggle__btn--active': isDenseView }" @click="viewMode = 'dense'">
                        Λίστα
                    </button>
                </div>
                <button type="button" class="story-chip" :class="{ 'story-chip--on': showSubs }" @click="showSubs = !showSubs">
                    <v-icon x-small>{{ showSubs ? 'mdi-check' : 'mdi-swap-horizontal' }}</v-icon>
                    Αλλαγές
                </button>
            </div>

            <div v-if="teamFilter !== 'all' && playerFilterOptions.length > 1" class="story-players">
                <button
                    v-for="option in playerFilterOptions"
                    :key="`story-player-${option.value}`"
                    type="button"
                    class="story-chip"
                    :class="{ 'story-chip--on': playerFilter === option.value }"
                    @click="playerFilter = option.value"
                >
                    {{ option.label }}
                </button>
            </div>
        </div>

        <!-- ---------- Compact list (also the printable export) ---------- -->
        <div v-if="isDenseView" class="storyline-export-wrap">
            <!-- the section title is already on the page; the printable export needs its own -->
            <div v-if="denseExport" class="storyline-export-header">
                <div class="storyline-export-title">Η ιστορία του αγώνα</div>
                <div class="storyline-export-score">
                    {{ team1DisplayName }} {{ finalScore.team1 }} - {{ finalScore.team2 }} {{ team2DisplayName }}
                </div>
            </div>

            <div v-if="!exportEventGroups.length" class="storyline-empty">
                Δεν υπάρχουν φάσεις ακόμα.
            </div>

            <div v-for="group in exportEventGroups" :key="`export-period-${group.period}`" class="storyline-export-period">
                <div class="storyline-export-period-title">{{ formatPeriodLabel(group.period) }}</div>
                <div v-for="event in group.events" :key="`export-${event.id}`" class="storyline-export-row" :class="`storyline-export-row--${event.kind}`">
                    <span class="export-clock">{{ event.clockLabel }}</span>
                    <span class="export-score">{{ event.showScore ? `${event.score1}-${event.score2}` : '' }}</span>
                    <span class="export-team">
                        <i class="export-team__dot" :style="{ background: teamColors[event.teamKey] || '#c4c4c8' }" />
                        {{ event.teamKey === 'team1' ? team1DisplayName : event.teamKey === 'team2' ? team2DisplayName : '' }}
                    </span>
                    <span class="export-player">{{ event.playerName }}</span>
                    <span class="export-action">{{ event.text }}</span>
                </div>
            </div>
        </div>

        <!-- ---------- Timeline ---------- -->
        <div v-else class="story-list">
            <div v-if="!displayItems.length" class="storyline-empty">
                Δεν υπάρχουν φάσεις για αυτή την επιλογή.
            </div>

            <template v-for="item in displayItems">
                <div v-if="item.type === 'period'" :key="item.id" class="story-period">
                    <span>{{ formatPeriodLabel(item.period) }}</span>
                </div>

                <article
                    v-else
                    :key="item.id"
                    class="story-event"
                    :class="[`story-event--${item.event.teamKey || 'neutral'}`, `story-event--${item.event.kind}`]"
                >
                    <div class="story-side story-side--left">
                        <div
                            v-if="item.event.teamKey === 'team1'"
                            class="story-card"
                            :style="{ '--team-color': teamColors.team1 }"
                        >
                            <span class="story-card__photo">
                                <img :src="item.event.playerImage || defaultPlayerImg" alt="" @error="$event.target.src = defaultPlayerImg">
                                <img v-if="team1LogoUrl" :src="team1LogoUrl" alt="" class="story-card__team">
                            </span>
                            <div class="story-card__copy">
                                <div class="story-card__player">{{ item.event.playerName }}</div>
                                <div class="story-card__action">{{ item.event.text }}</div>
                            </div>
                            <span v-if="item.event.kind === 'made'" class="story-card__points">+{{ item.event.points }}</span>
                        </div>
                    </div>

                    <div class="story-center">
                        <span class="story-clock">{{ item.event.clockLabel }}</span>
                        <span v-if="item.event.showScore" class="story-score">
                            <b :class="{ 'story-score--scored': item.event.teamKey === 'team1' }">{{ item.event.score1 }}</b>
                            -
                            <b :class="{ 'story-score--scored': item.event.teamKey === 'team2' }">{{ item.event.score2 }}</b>
                        </span>
                    </div>

                    <div class="story-side story-side--right">
                        <div
                            v-if="item.event.teamKey !== 'team1'"
                            class="story-card story-card--right"
                            :style="{ '--team-color': teamColors[item.event.teamKey] || '#c4c4c8' }"
                        >
                            <span class="story-card__photo">
                                <img :src="item.event.playerImage || defaultPlayerImg" alt="" @error="$event.target.src = defaultPlayerImg">
                                <img v-if="item.event.teamKey === 'team2' && team2LogoUrl" :src="team2LogoUrl" alt="" class="story-card__team">
                            </span>
                            <div class="story-card__copy">
                                <div class="story-card__player">{{ item.event.playerName }}</div>
                                <div class="story-card__action">{{ item.event.text }}</div>
                            </div>
                            <span v-if="item.event.kind === 'made'" class="story-card__points">+{{ item.event.points }}</span>
                        </div>
                    </div>
                </article>
            </template>
        </div>
    </div>
</template>

<script>
    import { getTeamColors } from '@/utils/shotChart'

    // what each stat key of a log means
    const ACTIONS = {
        twoPointsMade: { label: 'Εύστοχο δίποντο', kind: 'made' },
        threePointsMade: { label: 'Εύστοχο τρίποντο', kind: 'made' },
        freeThrowsMade: { label: 'Εύστοχη βολή', kind: 'made' },
        twoPointAttempts: { label: 'Άστοχο δίποντο', kind: 'miss' },
        threePointAttempts: { label: 'Άστοχο τρίποντο', kind: 'miss' },
        freeThrowAttempts: { label: 'Άστοχη βολή', kind: 'miss' },
        offensiveRebounds: { label: 'Επιθετικό ριμπάουντ', kind: 'play' },
        defensiveRebounds: { label: 'Αμυντικό ριμπάουντ', kind: 'play' },
        assists: { label: 'Ασίστ', kind: 'play' },
        steals: { label: 'Κλέψιμο', kind: 'play' },
        blockedShots: { label: 'Κόψιμο', kind: 'play' },
        turnovers: { label: 'Λάθος', kind: 'turnover' },
        personalFouls: { label: 'Προσωπικό φάουλ', kind: 'foul' },
        technicalFouls: { label: 'Τεχνική ποινή', kind: 'foul' },
        // before FIBA 2026
        unsportsmanlikeFouls: { label: 'Αντιαθλητικό φάουλ', kind: 'foul' },
        // FIBA 2026, in place of the unsportsmanlike foul
        disruptiveFouls: { label: 'Φάουλ σκοπιμότητας', kind: 'foul' },
        flagrantFouls: { label: 'Flagrant φάουλ', kind: 'foul' },
        // on the team, with no player
        coachTechnicalFouls: { label: 'Τεχνική ποινή προπονητή', kind: 'foul' },
        benchTechnicalFouls: { label: 'Τεχνική ποινή πάγκου', kind: 'foul' }
    }
    // an attempt is a miss only when the make is not in the same log
    const MISS_OF = { twoPointAttempts: 'twoPointsMade', threePointAttempts: 'threePointsMade', freeThrowAttempts: 'freeThrowsMade' }

    export default {
        name: 'MatchStoryline',
        props: {
            logs: {
                type: Array,
                default: () => []
            },
            match: {
                type: Object,
                default: () => ({})
            },
            team1Data: {
                type: Object,
                default: () => ({})
            },
            team2Data: {
                type: Object,
                default: () => ({})
            },
            denseExport: {
                type: Boolean,
                default: false
            }
        },
        data() {
            return {
                assetsUrl: 'https://storage.googleapis.com',
                defaultPlayerImg: require('@/assets/default_player.png'),
                selectedPeriod: null,
                // 'all' | 'team1' | 'team2'
                teamFilter: 'all',
                // within the selected team: 'all' | 'team' (team actions) | jersey number
                playerFilter: 'all',
                // substitutions are many and mostly noise, so they are hidden until asked for
                showSubs: false,
                viewMode: this.denseExport ? 'dense' : 'full'
            }
        },
        computed: {
            isDenseView() {
                return this.viewMode === 'dense'
            },
            team1() {
                return this.team1Data && Object.keys(this.team1Data).length
                    ? this.team1Data
                    : (this.match?.team1 || {})
            },
            team2() {
                return this.team2Data && Object.keys(this.team2Data).length
                    ? this.team2Data
                    : (this.match?.team2 || {})
            },
            team1DisplayName() {
                return this.team1.name || 'Γηπεδούχοι'
            },
            team2DisplayName() {
                return this.team2.name || 'Φιλοξενούμενοι'
            },
            teamColors() {
                return getTeamColors(this.team1, this.team2)
            },
            team1LogoUrl() {
                return this.getTeamLogoUrl('team1')
            },
            team2LogoUrl() {
                return this.getTeamLogoUrl('team2')
            },
            storylineEvents() {
                const normalizedLogs = this.normalizeLogsPayload(this.logs)
                if (!normalizedLogs.length) return []

                const sortedLogs = [...normalizedLogs]
                    .map((log, index) => ({ log, index }))
                    .sort((a, b) => {
                        const timestampA = this.getTimestampMs(a.log)
                        const timestampB = this.getTimestampMs(b.log)
                        if (timestampA !== null && timestampB !== null && timestampA !== timestampB) {
                            return timestampA - timestampB
                        }
                        if (timestampA !== null && timestampB === null) return -1
                        if (timestampA === null && timestampB !== null) return 1

                        const minuteA = this.getMinute(a.log)
                        const minuteB = this.getMinute(b.log)
                        if (minuteA === null && minuteB === null) return a.index - b.index
                        if (minuteA === null) return 1
                        if (minuteB === null) return -1
                        if (minuteA !== minuteB) return minuteA - minuteB
                        return a.index - b.index
                    })

                let runningScore1 = 0
                let runningScore2 = 0

                const events = sortedLogs.map(({ log, index }) => {
                    const snapshot = this.getSnapshotScores(log)
                    if (snapshot) {
                        runningScore1 = snapshot.score1
                        runningScore2 = snapshot.score2
                    } else {
                        const points = this.getPoints(log)
                        const pointsTeamKey = this.resolveTeamKey(log)
                        if (points > 0 && pointsTeamKey === 'team1') runningScore1 += points
                        if (points > 0 && pointsTeamKey === 'team2') runningScore2 += points
                    }

                    const actions = this.getLogActions(log)
                    const text = actions.length ? actions.map(action => action.label).join(' · ') : this.extractText(log)
                    if (!text) return null

                    const minute = this.getMinute(log)
                    const period = this.resolveEventPeriod(log, minute)
                    const teamKey = this.resolveTeamKey(log)

                    return {
                        id: `story-${index}`,
                        period,
                        minute: minute === null ? -1 : minute,
                        timestampMs: this.getTimestampMs(log),
                        orderIndex: index,
                        teamKey,
                        teamId: this.getTeamId(log),
                        teamName: this.getTeamName(log),
                        playerId: this.getPlayerId(log),
                        jerseyNumber: this.extractJerseyNumber(log),
                        playerName: this.extractActorLabel(log, teamKey),
                        playerImage: this.getPlayerImage(log, teamKey),
                        text,
                        kind: this.getEventKind(actions),
                        points: this.getPoints(log),
                        isShotEvent: this.isShotEvent(log),
                        showScore: this.shouldShowScore(log),
                        clockLabel: this.extractClockLabel(log, minute),
                        score1: runningScore1,
                        score2: runningScore2
                    }
                }).filter(Boolean)

                return events.sort((a, b) => {
                    if (a.timestampMs !== null && b.timestampMs !== null && a.timestampMs !== b.timestampMs) {
                        return b.timestampMs - a.timestampMs
                    }
                    if (a.timestampMs !== null && b.timestampMs === null) return -1
                    if (a.timestampMs === null && b.timestampMs !== null) return 1
                    if (a.period !== b.period) return b.period - a.period
                    if (a.minute !== b.minute) return b.minute - a.minute
                    return b.orderIndex - a.orderIndex
                })
            },
            availablePeriods() {
                const eventPeriods = Array.from(
                    new Set(this.jerseyFilteredEvents.map(event => event.period).filter(period => Number.isFinite(period)))
                ).sort((a, b) => a - b)

                const regular = [1, 2, 3, 4]
                const overtime = eventPeriods.filter(period => period >= 5)
                return [...regular, ...overtime]
            },
            periodFilterOptions() {
                return ['all', ...this.availablePeriods]
            },
            teamFilterOptions() {
                return [
                    { value: 'all', label: 'Όλες' },
                    { value: 'team1', label: this.team1DisplayName, logo: this.team1LogoUrl },
                    { value: 'team2', label: this.team2DisplayName, logo: this.team2LogoUrl }
                ]
            },
            // players of the selected team that appear in the logs, plus the team's own actions
            playerFilterOptions() {
                if (this.teamFilter === 'all') return []
                const teamEvents = this.storylineEvents.filter(event => this.eventMatchesTeam(event, this.teamFilter))
                const players = new Map()
                teamEvents.forEach((event) => {
                    if (event.playerId !== 0 && Number.isFinite(event.jerseyNumber) && !players.has(event.jerseyNumber)) {
                        players.set(event.jerseyNumber, event.playerName)
                    }
                })
                return [
                    { value: 'all', label: 'Όλοι' },
                    ...(teamEvents.some(event => event.playerId === 0) ? [{ value: 'team', label: 'Ομάδα' }] : []),
                    ...Array.from(players.entries())
                        .sort((a, b) => a[0] - b[0])
                        .map(([jersey, name]) => ({ value: jersey, label: name }))
                ]
            },
            jerseyFilteredEvents() {
                return this.storylineEvents.filter((event) => {
                    if (!this.showSubs && event.kind === 'sub') return false
                    if (this.teamFilter === 'all') {
                        return this.eventMatchesTeam(event, 'team1') || this.eventMatchesTeam(event, 'team2')
                    }
                    if (!this.eventMatchesTeam(event, this.teamFilter)) return false
                    return this.matchesJerseySelection(event, this.playerFilter)
                })
            },
            // the events with a heading before every period, when the whole match is shown
            displayItems() {
                const items = []
                let period = null
                this.filteredEvents.forEach((event) => {
                    if (this.selectedPeriod === 'all' && event.period !== period) {
                        period = event.period
                        items.push({ type: 'period', id: `period-${period}-${event.id}`, period })
                    }
                    items.push({ type: 'event', id: event.id, event })
                })
                return items
            },
            filteredEvents() {
                if (this.selectedPeriod === 'all') return this.jerseyFilteredEvents
                return this.jerseyFilteredEvents.filter(event => event.period === this.selectedPeriod)
            },
            finalScore() {
                const latest = this.storylineEvents[0]
                if (latest) {
                    return {
                        team1: latest.score1,
                        team2: latest.score2
                    }
                }

                return {
                    team1: this.getTeamTotal(this.team1),
                    team2: this.getTeamTotal(this.team2)
                }
            },
            scoreHighlightTeam() {
                if (this.finalScore.team1 > this.finalScore.team2) return 'team1'
                if (this.finalScore.team2 > this.finalScore.team1) return 'team2'
                return null
            },
            exportEventGroups() {
                const periods = this.selectedPeriod === 'all'
                    ? this.availablePeriods
                    : this.availablePeriods.filter(period => period === this.selectedPeriod)

                return periods
                    .map(period => ({
                        period,
                        events: this.jerseyFilteredEvents
                            .filter(event => event.period === period)
                            .sort((a, b) => {
                                if (a.timestampMs !== null && b.timestampMs !== null && a.timestampMs !== b.timestampMs) {
                                    return a.timestampMs - b.timestampMs
                                }
                                if (a.timestampMs !== null && b.timestampMs === null) return -1
                                if (a.timestampMs === null && b.timestampMs !== null) return 1
                                if (a.minute !== b.minute) return a.minute - b.minute
                                return a.orderIndex - b.orderIndex
                            })
                    }))
                    .filter(group => group.events.length)
            }
        },
        watch: {
            availablePeriods: {
                immediate: true,
                handler(periods) {
                    if (!periods.length) {
                        this.selectedPeriod = 'all'
                        return
                    }

                    if (this.selectedPeriod === 'all') return

                    if (!periods.includes(this.selectedPeriod)) {
                        this.selectedPeriod = periods[periods.length - 1]
                    }
                }
            }
        },
        methods: {
            normalizeLogsPayload(logs) {
                if (!logs) return []
                if (Array.isArray(logs)) return logs
                if (Array.isArray(logs.logs)) return logs.logs
                if (Array.isArray(logs.items)) return logs.items
                if (Array.isArray(logs.data)) return logs.data
                if (Array.isArray(logs.content)) return logs.content
                if (Array.isArray(logs.results)) return logs.results
                return []
            },
            toNumber(value) {
                const n = Number(value)
                return Number.isFinite(n) ? n : null
            },
            toCount(value) {
                if (value === true) return 1
                if (value === false || value === null || value === undefined) return 0
                const n = Number(value)
                return Number.isFinite(n) ? Math.max(0, n) : 0
            },
            setTeamFilter(value) {
                this.teamFilter = value
                this.playerFilter = 'all'
            },
            getTeamLogoUrl(teamKey) {
                const team = teamKey === 'team1' ? this.team1 : teamKey === 'team2' ? this.team2 : null
                const logo = team?.logo || null
                if (!logo) return null
                if (/^https?:\/\//i.test(logo)) return logo
                return `${this.assetsUrl}/${logo}`
            },
            eventMatchesTeam(event, teamKey) {
                const team = teamKey === 'team1' ? this.team1 : this.team2
                if (!team) return false

                if (event.teamKey === teamKey) return true
                if (event.teamId !== null && event.teamId !== undefined && this.idsMatch(event.teamId, team.id)) return true
                if (event.teamName && team.name && String(event.teamName).includes(String(team.name).toLowerCase())) return true

                return false
            },
            matchesJerseySelection(event, selectedJersey) {
                if (!this.isJerseySelectionDefined(selectedJersey)) return true
                if (selectedJersey === 'all') return true
                if (selectedJersey === 'team') return event.playerId === 0
                if (selectedJersey === 0) return event.playerId !== 0 && event.jerseyNumber === 0
                return event.playerId !== 0 && event.jerseyNumber === selectedJersey
            },
            isJerseySelectionDefined(selectedJersey) {
                return selectedJersey !== null && selectedJersey !== undefined && selectedJersey !== ''
            },
            getLogSource(log) {
                const nested = [log?.data, log?.stats, log?.event, log?.payload].find(
                    value => value && typeof value === 'object'
                ) || {}

                return {
                    ...nested,
                    ...log
                }
            },
            // the stat keys of a log as Greek actions
            getLogActions(log) {
                const source = this.getLogSource(log)
                const actions = []
                Object.keys(source).forEach((key) => {
                    if (key === 'playerMatchStatus') {
                        if (source[key] === 'begin') actions.push({ label: 'Μπαίνει στο παρκέ', kind: 'sub' })
                        if (source[key] === 'stop') actions.push({ label: 'Βγαίνει από το παρκέ', kind: 'sub' })
                        return
                    }
                    const action = ACTIONS[key]
                    if (!action || this.toCount(source[key]) <= 0) return
                    if (MISS_OF[key] && this.toCount(source[MISS_OF[key]]) > 0) return
                    actions.push(action)
                })
                return actions
            },
            getEventKind(actions) {
                const order = ['made', 'foul', 'turnover', 'miss', 'play', 'sub']
                return order.find(kind => actions.some(action => action.kind === kind)) || 'play'
            },
            extractText(log) {
                const source = this.getLogSource(log)
                const parts = [
                    source.description,
                    source.message,
                    source.action,
                    source.eventType,
                    source.type,
                    source.title,
                    source.text,
                    source.log,
                    source.label
                ].filter(Boolean)

                return parts.join(' ').replace(/\s+/g, ' ').trim()
            },
            formatTeamActorName(name, fallbackLabel) {
                const trimmed = String(name || '').trim()
                if (!trimmed) return fallbackLabel
                return trimmed
            },
            extractActorLabel(log, teamKey = null) {
                const source = this.getLogSource(log)
                const playerId = this.toNumber(source.playerId)
                if (playerId === 0) {
                    if (teamKey === 'team1') return this.formatTeamActorName(this.team1.name, 'Γηπεδούχοι')
                    if (teamKey === 'team2') return this.formatTeamActorName(this.team2.name, 'Φιλοξενούμενοι')
                    return 'Ομάδα'
                }

                const jersey = source.alternativeJersey || source.alternativeJersey === 0
                    ? source.alternativeJersey
                    : source.jersey
                const rosterPlayer = this.findRosterPlayer(playerId, teamKey)
                const surname = rosterPlayer ? (rosterPlayer.surname || rosterPlayer.lastName || rosterPlayer.name || '') : ''
                if (jersey || jersey === 0) return surname ? `#${jersey} ${surname}` : `#${jersey}`
                if (surname) return surname

                const explicitPlayer = source.playerName || source.player || source.athleteName || source.name || null
                if (explicitPlayer) return String(explicitPlayer)

                if (teamKey === 'team1') return this.team1DisplayName
                if (teamKey === 'team2') return this.team2DisplayName
                return 'Αγώνας'
            },
            findRosterPlayer(playerId, teamKey = null) {
                if (playerId === null || playerId === undefined) return null
                const teams = teamKey === 'team2' ? [this.team2, this.team1] : [this.team1, this.team2]
                for (const team of teams) {
                    const players = Array.isArray(team?.players) ? team.players : []
                    const found = players.find(player => this.idsMatch(playerId, player?.id ?? player?.playerId))
                    if (found) return found
                }
                return null
            },
            extractJerseyNumber(log) {
                const source = this.getLogSource(log)
                const direct = this.toNumber(source.alternativeJersey ?? source.jersey)
                const playerId = this.getPlayerId(log)
                if (playerId === 0 && direct === 0) return null
                if (direct !== null) return direct

                const actorLabel = this.extractActorLabel(log)
                if (typeof actorLabel !== 'string') return null
                const match = actorLabel.match(/^#(\d{1,3})\b/)
                if (!match) return null
                return Number(match[1])
            },
            getPlayerId(log) {
                const source = this.getLogSource(log)
                return this.toNumber(
                    source.playerId ?? source.player?.id ?? source.athlete?.id ?? source.playerInfo?.id ?? source.personId ?? source.person?.id
                )
            },
            getPlayerImage(log, teamKey = null) {
                const source = this.getLogSource(log)
                const playerId = this.getPlayerId(log)

                const teamCandidates = []
                if (teamKey === 'team1') teamCandidates.push(this.team1)
                if (teamKey === 'team2') teamCandidates.push(this.team2)
                if (!teamCandidates.includes(this.team1)) teamCandidates.push(this.team1)
                if (!teamCandidates.includes(this.team2)) teamCandidates.push(this.team2)

                if (playerId !== null && playerId !== undefined) {
                    for (const team of teamCandidates) {
                        const players = Array.isArray(team?.players) ? team.players : []
                        const matched = players.find((player) => this.idsMatch(
                            playerId,
                            player?.id ?? player?.playerId ?? player?.athleteId ?? player?.personId
                        ))

                        if (matched) {
                            const teamPlayerImage = matched.image || matched.photo || matched.avatar || matched.imageUrl ||
                                matched.player?.image || matched.player?.photo || matched.player?.avatar || matched.player?.imageUrl ||
                                matched.athlete?.image || matched.athlete?.photo || matched.athlete?.avatar || matched.athlete?.imageUrl

                            if (teamPlayerImage) {
                                if (/^https?:\/\//i.test(teamPlayerImage)) return teamPlayerImage
                                return `${this.assetsUrl}/${teamPlayerImage}`
                            }
                        }
                    }
                }

                const playerSources = [
                    source.player,
                    source.athlete,
                    source.playerInfo,
                    log?.player,
                    log?.athlete,
                    log?.data?.player,
                    log?.event?.player,
                    log?.payload?.player,
                    source
                ].filter(value => value && typeof value === 'object')

                for (const playerSource of playerSources) {
                    const raw = playerSource.image || playerSource.photo || playerSource.avatar || playerSource.imageUrl || null
                    if (!raw) continue
                    if (/^https?:\/\//i.test(raw)) return raw
                    return `${this.assetsUrl}/${raw}`
                }

                return this.defaultPlayerImg
            },
            isShotEvent(log) {
                const source = this.getLogSource(log)
                return this.toCount(source.freeThrowsMade ?? source.freeThrowMade) > 0 ||
                    this.toCount(source.freeThrowAttempts) > 0 ||
                    this.toCount(source.twoPointsMade ?? source.twoPointMade) > 0 ||
                    this.toCount(source.twoPointAttempts) > 0 ||
                    this.toCount(source.threePointsMade ?? source.threePointMade) > 0 ||
                    this.toCount(source.threePointAttempts) > 0
            },
            shouldShowScore(log) {
                const source = this.getLogSource(log)
                return this.toCount(source.freeThrowsMade ?? source.freeThrowMade) > 0 ||
                    this.toCount(source.twoPointsMade ?? source.twoPointMade) > 0 ||
                    this.toCount(source.threePointsMade ?? source.threePointMade) > 0 ||
                    this.getPoints(log) > 0
            },
            getPeriod(log) {
                const source = this.getLogSource(log)
                return this.toNumber(source.period ?? source.quarter ?? source.q ?? source.currentPeriod)
            },
            getElapsedClockMinute(log) {
                const elapsedClockSeconds = this.getElapsedClockSeconds(log)
                if (Number.isFinite(elapsedClockSeconds)) {
                    return Math.max(0, Math.floor(elapsedClockSeconds / 60))
                }

                const source = this.getLogSource(log)
                const candidates = [
                    source.clock,
                    source.time,
                    source.timeLeft,
                    source.timer,
                    source.timestamp,
                    source.timeStamp,
                    source.matchClock,
                    source.gameClock,
                    source.displayClock,
                    source.matchTime,
                    source.gameTime,
                    source.minute
                ].filter(value => value !== null && value !== undefined && value !== '')

                for (const candidate of candidates) {
                    if (typeof candidate === 'number' && Number.isFinite(candidate)) {
                        // Some feeds encode elapsed time as MMSS (e.g. 4400 => 44:00).
                        if (candidate >= 1000 && candidate <= 9959) {
                            const mm = Math.floor(candidate / 100)
                            const ss = candidate % 100
                            if (mm > 10 && ss >= 0 && ss < 60) {
                                return Math.max(0, Math.floor(mm + (ss / 60)))
                            }
                        }

                        // Direct elapsed minute format.
                        if (candidate > 10 && candidate < 200) {
                            return Math.max(0, Math.floor(candidate))
                        }
                        continue
                    }

                    if (typeof candidate !== 'string') continue
                    const text = candidate.trim()
                    if (!text) continue

                    const timeMatch = text.match(/^\s*(\d{1,3})[:.](\d{2})\s*$/)
                    if (timeMatch) {
                        const mm = this.toNumber(timeMatch[1])
                        const ss = this.toNumber(timeMatch[2])
                        if (mm !== null && ss !== null && mm > 10 && ss >= 0 && ss < 60) {
                            return Math.max(0, Math.floor(mm + (ss / 60)))
                        }
                    }

                    // MMSS compact strings (e.g. "4400").
                    const compactMatch = text.match(/^\s*(\d{3,4})\s*$/)
                    if (compactMatch) {
                        const n = Number(compactMatch[1])
                        if (Number.isFinite(n)) {
                            const mm = Math.floor(n / 100)
                            const ss = n % 100
                            if (mm > 10 && ss >= 0 && ss < 60) {
                                return Math.max(0, Math.floor(mm + (ss / 60)))
                            }
                        }
                    }
                }

                return null
            },
            getElapsedClockSeconds(log) {
                const source = this.getLogSource(log)
                const candidates = [
                    source.clock,
                    source.time,
                    source.timeLeft,
                    source.timer,
                    source.timestamp,
                    source.timeStamp,
                    source.matchClock,
                    source.gameClock,
                    source.displayClock,
                    source.matchTime,
                    source.gameTime,
                    source.minute
                ].filter(value => value !== null && value !== undefined && value !== '')

                for (const candidate of candidates) {
                    if (typeof candidate === 'number' && Number.isFinite(candidate)) {
                        // Some feeds encode elapsed time as MMSS (e.g. 4400 => 44:00).
                        if (candidate >= 1000 && candidate <= 9959) {
                            const mm = Math.floor(candidate / 100)
                            const ss = candidate % 100
                            if (mm > 10 && ss >= 0 && ss < 60) {
                                return Math.max(0, (mm * 60) + ss)
                            }
                        }

                        // Some feeds encode elapsed time as MMSSxx (e.g. 400000 => 40:00:00).
                        if (candidate >= 100000 && candidate <= 995959) {
                            const mm = Math.floor(candidate / 10000)
                            const ss = Math.floor(candidate / 100) % 100
                            if (mm > 10 && ss >= 0 && ss < 60) {
                                return Math.max(0, (mm * 60) + ss)
                            }
                        }

                        // Direct elapsed minute format.
                        if (candidate > 10 && candidate < 200) {
                            return Math.max(0, Math.floor(candidate * 60))
                        }
                        continue
                    }

                    if (typeof candidate !== 'string') continue
                    const text = candidate.trim()
                    if (!text) continue

                    const timeMatch = text.match(/^\s*(\d{1,3})[:.](\d{2})(?:[:.](\d{2}))?\s*$/)
                    if (timeMatch) {
                        const part1 = this.toNumber(timeMatch[1])
                        const part2 = this.toNumber(timeMatch[2])
                        const part3 = this.toNumber(timeMatch[3])

                        if (part1 !== null && part2 !== null) {
                            // 00:40:00 is typically HH:MM:SS, while 40:00:00 is MM:SS:xx.
                            if (part3 !== null && part1 === 0 && part2 >= 0 && part2 < 60 && part3 >= 0 && part3 < 60) {
                                return Math.max(0, (part2 * 60) + part3)
                            }

                            if (part1 > 10 && part2 >= 0 && part2 < 60) {
                                return Math.max(0, (part1 * 60) + part2)
                            }
                        }
                    }

                    // MMSS or MMSSxx compact strings (e.g. "4400", "400000").
                    const compactMatch = text.match(/^\s*(\d{3,6})\s*$/)
                    if (compactMatch) {
                        const n = Number(compactMatch[1])
                        if (Number.isFinite(n)) {
                            if (compactMatch[1].length >= 5) {
                                const mm = Math.floor(n / 10000)
                                const ss = Math.floor(n / 100) % 100
                                if (mm > 10 && ss >= 0 && ss < 60) {
                                    return Math.max(0, (mm * 60) + ss)
                                }
                            } else {
                                const mm = Math.floor(n / 100)
                                const ss = n % 100
                                if (mm > 10 && ss >= 0 && ss < 60) {
                                    return Math.max(0, (mm * 60) + ss)
                                }
                            }
                        }
                    }
                }

                return null
            },
            resolveEventPeriod(log, minute = null) {
                const sourcePeriod = this.getPeriod(log)
                const clockLabelPeriod = this.periodFromClockLabel(this.extractClockLabel(log, minute))
                const elapsedClockSeconds = this.getElapsedClockSeconds(log)
                const elapsedClockMinute = this.getElapsedClockMinute(log)
                const secondPeriod = this.periodFromElapsedSeconds(elapsedClockSeconds)
                const minutePeriod = this.periodFromMinute(
                    Number.isFinite(elapsedClockMinute) ? elapsedClockMinute : minute
                )
                const derivedPeriod = Number.isFinite(clockLabelPeriod)
                    ? clockLabelPeriod
                    : (Number.isFinite(secondPeriod) ? secondPeriod : minutePeriod)

                // Some feeds use 0-based periods. Prefer minute-derived value when available.
                if (sourcePeriod === 0 && Number.isFinite(derivedPeriod)) return derivedPeriod
                if (sourcePeriod === 0 && !Number.isFinite(derivedPeriod)) return 1

                // When a reliable elapsed clock exists, prefer its derived period.
                if (Number.isFinite(derivedPeriod) && Number.isFinite(sourcePeriod)) return derivedPeriod

                if (!Number.isFinite(sourcePeriod) && Number.isFinite(derivedPeriod)) return derivedPeriod
                if (Number.isFinite(sourcePeriod) && !Number.isFinite(derivedPeriod)) return sourcePeriod
                if (!Number.isFinite(sourcePeriod) && !Number.isFinite(derivedPeriod)) return 1

                // Some feeds keep period at 1 while minute is in OT; prefer minute-derived OT.
                if (derivedPeriod >= 5 && sourcePeriod <= 4) return derivedPeriod

                return sourcePeriod
            },
            getMinute(log) {
                const source = this.getLogSource(log)
                const elapsedClockMinute = this.getElapsedClockMinute(log)
                if (Number.isFinite(elapsedClockMinute)) return elapsedClockMinute

                const directMinute = this.toNumber(source.minute ?? source.matchMinute ?? source.gameMinute ?? source.min)
                if (directMinute !== null) return Math.max(0, Math.floor(directMinute))

                const elapsedSeconds = this.toNumber(source.elapsedSeconds ?? source.matchSecond ?? source.gameSecond ?? source.second)
                if (elapsedSeconds !== null) return Math.max(0, Math.floor(elapsedSeconds / 60))

                const clock = source.clock ?? source.time ?? source.timeLeft ?? source.timer
                if (clock && typeof clock === 'string') {
                    const match = clock.match(/(\d{1,2}):(\d{2})/)
                    if (match) {
                        const clockMinutes = this.toNumber(match[1])
                        const clockSeconds = this.toNumber(match[2])
                        const period = this.getPeriod(source)

                        if (clockMinutes !== null && clockSeconds !== null && period !== null) {
                            const elapsedInPeriod = Math.max(0, (10 * 60) - (clockMinutes * 60 + clockSeconds))
                            return Math.max(0, Math.floor(((period - 1) * 10) + (elapsedInPeriod / 60)))
                        }
                    }
                }

                return null
            },
            getTimestampMs(log) {
                const source = this.getLogSource(log)
                const candidates = [
                    source.timestamp,
                    source.timeStamp,
                    source.createdAt,
                    source.eventTimestamp,
                    source.eventTime,
                    source.datetime,
                    source.date,
                    source.time
                ]

                for (const candidate of candidates) {
                    if (candidate === null || candidate === undefined || candidate === '') continue

                    if (typeof candidate === 'number' && Number.isFinite(candidate)) {
                        return candidate < 1e12 ? candidate * 1000 : candidate
                    }

                    const asNumber = Number(candidate)
                    if (Number.isFinite(asNumber)) {
                        return asNumber < 1e12 ? asNumber * 1000 : asNumber
                    }

                    if (typeof candidate === 'string') {
                        const parsed = Date.parse(candidate)
                        if (Number.isFinite(parsed)) return parsed
                    }
                }

                return null
            },
            periodFromMinute(minute) {
                if (!Number.isFinite(minute) || minute < 0) return null
                if (minute <= 40) return Math.max(1, Math.ceil(minute / 10))
                return 4 + Math.ceil((minute - 40) / 5)
            },
            periodFromElapsedSeconds(seconds) {
                if (!Number.isFinite(seconds) || seconds < 0) return null
                if (seconds <= 2400) return Math.max(1, Math.ceil(seconds / 600))
                return 4 + Math.ceil((seconds - 2400) / 300)
            },
            periodFromClockLabel(clockLabel) {
                if (typeof clockLabel !== 'string') return null
                const match = clockLabel.trim().match(/^(\d{1,3}):(\d{2})$/)
                if (!match) return null

                const mm = this.toNumber(match[1])
                const ss = this.toNumber(match[2])
                if (mm === null || ss === null || ss < 0 || ss >= 60) return null

                // Only treat labels beyond 10:00 as elapsed-game timeline values.
                if (mm <= 10) return null

                return this.periodFromElapsedSeconds((mm * 60) + ss)
            },
            extractClockLabel(log, minute) {
                const source = this.getLogSource(log)
                const candidates = [source.clock, source.time, source.timeLeft, source.timer, source.timestamp, source.timeStamp]
                    .filter(Boolean)

                for (const candidate of candidates) {
                    if (typeof candidate !== 'string') continue
                    const match = candidate.match(/(\d{1,3}:\d{2})(?::\d{2})?$/)
                    if (match) return match[1]
                }

                if (Number.isFinite(minute) && minute >= 0) return `M${minute}`
                return '--:--'
            },
            getTeamId(log) {
                const source = this.getLogSource(log)
                return source.teamId ??
                    source.team_id ??
                    source.team?.id ??
                    source.team?.teamId ??
                    source.clubId ??
                    source.club?.id ??
                    source.competitorId ??
                    source.competitor?.id ??
                    null
            },
            idsMatch(a, b) {
                if (a === null || a === undefined || b === null || b === undefined) return false

                const aNum = Number(a)
                const bNum = Number(b)
                if (Number.isFinite(aNum) && Number.isFinite(bNum)) {
                    return aNum === bNum
                }

                return String(a).trim() === String(b).trim()
            },
            getTeamName(log) {
                const source = this.getLogSource(log)
                const name = source.teamName || source.team?.name || source.clubName || source.teamLabel || ''
                return name ? String(name).toLowerCase() : ''
            },
            resolveTeamKey(log) {
                const source = this.getLogSource(log)
                const teamId = this.getTeamId(log)
                if (teamId !== null) {
                    if (this.idsMatch(teamId, this.team1?.id)) return 'team1'
                    if (this.idsMatch(teamId, this.team2?.id)) return 'team2'
                }

                const homeAway = String(source.homeAway || source.side || source.teamSide || '').toLowerCase()
                if (homeAway === 'home' || homeAway === 'team1') return 'team1'
                if (homeAway === 'away' || homeAway === 'team2') return 'team2'
                if (source.isHome === true) return 'team1'
                if (source.isHome === false) return 'team2'

                const teamName = this.getTeamName(log)
                if (teamName && this.team1?.name && teamName.includes(String(this.team1.name).toLowerCase())) return 'team1'
                if (teamName && this.team2?.name && teamName.includes(String(this.team2.name).toLowerCase())) return 'team2'

                return null
            },
            getPoints(log) {
                const source = this.getLogSource(log)
                const directPoints = this.toNumber(source.points ?? source.point ?? source.scoreValue ?? source.value)
                if (directPoints !== null) return Math.max(0, directPoints)

                const freeThrowsMade = this.toCount(source.freeThrowsMade ?? source.freeThrowMade)
                const threePointsMade = this.toCount(source.threePointsMade ?? source.threePointMade)
                const twoPointsMade = this.toCount(source.twoPointsMade ?? source.twoPointMade)
                const totalMade = freeThrowsMade + threePointsMade + twoPointsMade
                if (totalMade <= 0) return 0

                return freeThrowsMade + (threePointsMade * 3) + (twoPointsMade * 2)
            },
            getSnapshotScores(log) {
                const source = this.getLogSource(log)
                const score1 = this.toNumber(source.scoreTeam1 ?? source.team1Score ?? source.homeScore)
                const score2 = this.toNumber(source.scoreTeam2 ?? source.team2Score ?? source.awayScore)
                if (score1 !== null && score2 !== null) {
                    return { score1, score2 }
                }

                const nested = source.score || source.currentScore
                if (nested && typeof nested === 'object') {
                    const nestedScore1 = this.toNumber(nested.scoreTeam1 ?? nested.team1Score ?? nested.homeScore)
                    const nestedScore2 = this.toNumber(nested.scoreTeam2 ?? nested.team2Score ?? nested.awayScore)
                    if (nestedScore1 !== null && nestedScore2 !== null) {
                        return { score1: nestedScore1, score2: nestedScore2 }
                    }
                }

                const scoreText = String(source.scoreText || source.score || '')
                const match = scoreText.match(/(\d{1,3})\s*[-:]\s*(\d{1,3})/)
                if (match) {
                    return {
                        score1: Number(match[1]),
                        score2: Number(match[2])
                    }
                }

                return null
            },
            getTeamTotal(team = {}) {
                const direct = this.toNumber(team.score ?? team.points ?? team.teamScore ?? team.totalPoints)
                if (direct !== null) return direct

                const players = Array.isArray(team.players) ? team.players : []
                return players.reduce((sum, player) => {
                    const points = this.toNumber(player?.stats?.[0]?.points)
                    return sum + (points === null ? 0 : points)
                }, 0)
            },
            formatPeriodLabel(period) {
                if (!Number.isFinite(period)) return 'Περίοδος'
                if (period <= 4) return `${period}η περίοδος`
                return `Παράταση ${period - 4}`
            },
            formatPeriodFilterLabel(period) {
                if (period === 'all') return 'Όλος ο αγώνας'
                return period <= 4 ? `${period}η` : `Παρ. ${period - 4}`
            }
        }
    }
</script>

<style scoped>
.storyline-wrap {
    display: flex;
    flex-direction: column;
    gap: 14px;
    width: 100%;
    font-family: Ubuntu, Arial, sans-serif;
}

/* ---------- Filters ---------- */
.story-controls {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
}

.story-controls__row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;
    gap: 10px;
    max-width: 100%;
}

.story-toggle {
    display: inline-flex;
    gap: 2px;
    max-width: 100%;
    padding: 3px;
    border-radius: 999px;
    background: #0d0d0f;
    overflow-x: auto;
    scrollbar-width: none;
}

.story-toggle::-webkit-scrollbar {
    display: none;
}

.story-toggle__btn {
    display: inline-flex;
    flex: 0 0 auto;
    align-items: center;
    gap: 6px;
    max-width: 220px;
    padding: 6px 14px;
    border-radius: 999px;
    color: rgba(255, 255, 255, 0.75);
    font-size: 0.8rem;
    font-weight: 700;
    white-space: nowrap;
    transition: background 0.2s ease, color 0.2s ease;
}

.story-toggle__btn span {
    overflow: hidden;
    text-overflow: ellipsis;
}

.story-toggle__btn:hover {
    color: #fff;
}

.story-toggle__btn--active {
    background: linear-gradient(90deg, #ef4444, #ff7133, orange);
    color: #fff;
}

.story-toggle__logo {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: #fff;
    object-fit: contain;
}

.story-chip {
    display: inline-flex;
    flex: 0 0 auto;
    align-items: center;
    gap: 4px;
    padding: 5px 12px;
    border: 1px solid rgba(0, 0, 0, 0.12);
    border-radius: 999px;
    color: rgba(0, 0, 0, 0.65);
    font-size: 0.78rem;
    font-weight: 700;
    white-space: nowrap;
}

.story-chip .v-icon {
    color: inherit;
}

.story-chip--on {
    border-color: transparent;
    background: #0d0d0f;
    color: #fff;
}

.story-players {
    display: flex;
    gap: 6px;
    max-width: 100%;
    padding: 2px;
    overflow-x: auto;
    scrollbar-width: none;
}

.story-players::-webkit-scrollbar {
    display: none;
}

.storyline-empty {
    padding: 24px 0;
    color: rgba(0, 0, 0, 0.45);
    font-size: 0.9rem;
    text-align: center;
}

/* ---------- Timeline ---------- */
.story-list {
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 6px;
}

/* the line running behind the clocks */
.story-list::before {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: 50%;
    width: 2px;
    transform: translateX(-50%);
    background: rgba(0, 0, 0, 0.07);
}

.story-period {
    position: relative;
    z-index: 1;
    display: flex;
    justify-content: center;
    margin: 10px 0 4px;
}

.story-period span {
    padding: 5px 16px;
    border-radius: 999px;
    background: #0d0d0f;
    color: #fff;
    font-size: 0.75rem;
    font-weight: 800;
    letter-spacing: 0.06em;
    text-transform: uppercase;
}

.story-event {
    position: relative;
    z-index: 1;
    display: grid;
    grid-template-columns: minmax(0, 1fr) 96px minmax(0, 1fr);
    align-items: center;
    gap: 10px;
}

.story-side {
    display: flex;
    min-width: 0;
}

.story-side--left {
    justify-content: flex-end;
}

.story-center {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 3px;
}

.story-clock {
    padding: 2px 9px;
    border: 1px solid rgba(0, 0, 0, 0.1);
    border-radius: 999px;
    background: #fff;
    color: rgba(0, 0, 0, 0.6);
    font-size: 0.75rem;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
}

.story-score {
    padding: 2px 10px;
    border-radius: 8px;
    background: #0d0d0f;
    color: rgba(255, 255, 255, 0.6);
    font-size: 0.85rem;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
}

.story-score b {
    color: #fff;
}

.story-score b.story-score--scored {
    color: #ff8a3d;
}

/* ---------- Event card ---------- */
.story-card {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    max-width: 380px;
    padding: 6px 12px 6px 6px;
    border: 1px solid rgba(0, 0, 0, 0.06);
    border-left: 4px solid var(--team-color);
    border-radius: 12px;
    background: #fff;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
}

.story-card--right {
    flex-direction: row-reverse;
    padding: 6px 6px 6px 12px;
    border-left: 1px solid rgba(0, 0, 0, 0.06);
    border-right: 4px solid var(--team-color);
    text-align: right;
}

.story-card__photo {
    position: relative;
    flex: 0 0 auto;
    width: 40px;
    height: 40px;
}

.story-card__photo > img:first-child {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: #f1f1f3;
    object-fit: cover;
    object-position: top;
}

.story-card__team {
    position: absolute;
    right: -4px;
    bottom: -3px;
    width: 18px;
    height: 18px;
    padding: 1px;
    border-radius: 50%;
    background: #fff;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
    object-fit: contain;
}

.story-card__copy {
    flex: 1;
    min-width: 0;
    line-height: 1.2;
}

.story-card__player {
    overflow: hidden;
    color: #2b2b2e;
    font-size: 0.88rem;
    font-weight: 800;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.story-card__action {
    color: rgba(0, 0, 0, 0.55);
    font-size: 0.8rem;
    font-weight: 600;
}

.story-card__points {
    flex: 0 0 auto;
    padding: 3px 8px;
    border-radius: 8px;
    background: linear-gradient(90deg, #ef4444, #ff7133, orange);
    color: #fff;
    font-size: 0.85rem;
    font-weight: 800;
}

/* what happened decides how loud the card is */
.story-event--made .story-card {
    background: #fff8f2;
}

.story-event--made .story-card__action {
    color: #e2551f;
    font-weight: 800;
}

.story-event--foul .story-card__action,
.story-event--turnover .story-card__action {
    color: #c62828;
}

.story-event--sub .story-card,
.story-event--miss .story-card {
    box-shadow: none;
}

.story-event--sub .story-card {
    opacity: 0.7;
}

/* ---------- Compact list ---------- */
.storyline-export-wrap {
    display: flex;
    flex-direction: column;
    gap: 14px;
}

.storyline-export-header {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    justify-content: space-between;
    gap: 6px 16px;
    padding-bottom: 8px;
    border-bottom: 2px solid #0d0d0f;
}

.storyline-export-title {
    color: #2b2b2e;
    font-size: 1.05rem;
    font-weight: 800;
}

.storyline-export-score {
    color: rgba(0, 0, 0, 0.6);
    font-size: 0.9rem;
    font-weight: 700;
}

.storyline-export-period-title {
    margin-bottom: 4px;
    color: #e2551f;
    font-size: 0.75rem;
    font-weight: 800;
    letter-spacing: 0.06em;
    text-transform: uppercase;
}

.storyline-export-row {
    display: grid;
    grid-template-columns: 52px 60px minmax(90px, 160px) minmax(110px, 200px) 1fr;
    align-items: center;
    gap: 10px;
    padding: 5px 4px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.05);
    color: #2b2b2e;
    font-size: 0.82rem;
}

.storyline-export-row--made {
    background: #fff8f2;
}

.export-clock,
.export-score {
    color: rgba(0, 0, 0, 0.55);
    font-weight: 700;
    font-variant-numeric: tabular-nums;
}

.storyline-export-row--made .export-score {
    color: #111;
}

.export-team {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
    overflow: hidden;
    color: rgba(0, 0, 0, 0.6);
    white-space: nowrap;
    text-overflow: ellipsis;
}

.export-team__dot {
    flex: 0 0 auto;
    width: 8px;
    height: 8px;
    border-radius: 50%;
}

.export-player {
    overflow: hidden;
    font-weight: 800;
    white-space: nowrap;
    text-overflow: ellipsis;
}

.export-action {
    color: rgba(0, 0, 0, 0.65);
}

/* ---------- Phone: one column, clock on the left ---------- */
@media (max-width: 600px) {
    .story-team-switch {
        width: 100%;
    }

    .story-team-switch .story-toggle__btn {
        flex: 1 1 auto;
        justify-content: center;
        min-width: 0;
        padding: 6px 10px;
    }

    .story-list::before {
        left: 26px;
    }

    .story-event {
        grid-template-columns: 52px minmax(0, 1fr);
        gap: 8px;
    }

    .story-center {
        order: -1;
    }

    .story-side:empty {
        display: none;
    }

    .story-side--left {
        justify-content: flex-start;
    }

    .story-card,
    .story-card--right {
        flex-direction: row;
        max-width: none;
        padding: 6px 10px 6px 6px;
        border-left: 4px solid var(--team-color);
        border-right: 1px solid rgba(0, 0, 0, 0.06);
        text-align: left;
    }

    .story-clock {
        padding: 2px 6px;
        font-size: 0.7rem;
    }

    .story-score {
        padding: 2px 6px;
        font-size: 0.75rem;
    }

    .story-period {
        justify-content: flex-start;
    }

    .storyline-export-row {
        grid-template-columns: 44px 48px minmax(0, 1fr);
        gap: 4px 8px;
    }

    .export-team {
        display: none;
    }

    .export-action {
        grid-column: 3;
    }
}

@media print {
    .story-controls {
        display: none;
    }
}
</style>
